import React from "react";
import { Link, Navigate, Route, Routes, useParams } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import { ArrowBackIcon } from "../../util/Icons";
import SubMenuWrapper, { SubMenuItem } from "../../util/SubMenu";
import monthlyMeetupsData from "../data/monthlyMeetupsData";

export default function MonthlyMeetups() {
    const { membership } = useUserContext();
    const isMember = membership === true;

    if (!isMember) {
        return <MembersOnlyMeetupsLock />;
    }

    return (
        <Routes>
            <Route path="/" element={<MeetupsIndex />} />
            <Route path="/:meetupId" element={<MeetupDetailWrapper />} />
            <Route path="/*" element={<Navigate to="/strong-healthy/monthly-meetups" replace />} />
        </Routes>
    );
}

function MembersOnlyMeetupsLock() {
    return (
        <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: "#ffffff" }}>
            <div className="max-w-4xl mx-auto">
                <SubMenuWrapper>
                    <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Health Hub"} to={"/strong-healthy"} />
                </SubMenuWrapper>

                <div className="text-center mt-8">
                    <div
                        className="rounded-3xl p-8"
                        style={{
                            background: "linear-gradient(135deg, #f8fafc 0%, #e8f1fb 100%)",
                            border: "1px solid rgba(0, 107, 193, 0.14)",
                        }}
                    >
                        <span className="text-6xl mb-4 block">🔒</span>
                        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1e293b" }}>
                            Members Only Meetups
                        </h2>
                        <p className="text-base mb-6" style={{ color: "#64748b" }}>
                            Monthly Meetups are available only to Momentum Members.
                        </p>
                        <Link
                            to="/membership"
                            className="inline-block rounded-2xl px-8 py-4 font-bold text-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl no-underline"
                            style={{
                                background: "linear-gradient(135deg, #006bc1 0%, #0e89ef 100%)",
                                color: "white",
                                boxShadow: "0 10px 40px rgba(0, 107, 193, 0.24)",
                            }}
                        >
                            Become a Member
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MeetupsIndex() {
    return (
        <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: "#ffffff" }}>
            <div className="max-w-4xl mx-auto">
                <SubMenuWrapper>
                    <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Health Hub"} to={"/strong-healthy"} />
                </SubMenuWrapper>

                <div className="text-center mb-6 mt-4">
                    <span
                        className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-3"
                        style={{
                            background: "linear-gradient(90deg, #0ea5e9 0%, #2563eb 100%)",
                            color: "white",
                        }}
                    >
                        Monthly Meetups
                    </span>
                    <h1
                        className="text-3xl md:text-4xl font-extrabold mb-3"
                        style={{
                            background: "linear-gradient(90deg, #1e293b 0%, #334155 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        Monthly Meetups
                    </h1>
                    <p className="text-base md:text-lg" style={{ color: "#64748b" }}>
                        Watch the latest member meetups and revisit the key notes from each session.
                    </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    {monthlyMeetupsData.map((meetup) => (
                        <MeetupCard key={meetup.id} meetup={meetup} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function MeetupCard({ meetup }) {
    return (
        <Link to={`/strong-healthy/monthly-meetups/${meetup.id}`} className="no-underline block h-full">
            <div
                className="rounded-2xl p-5 md:p-6 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer h-full"
                style={{
                    background: meetup.comingSoon
                        ? "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
                        : "linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)",
                    border: "1px solid rgba(14, 165, 233, 0.18)",
                }}
            >
                <div className="flex items-start gap-4">
                    <span className="text-4xl" aria-hidden="true">🎥</span>
                    <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span
                                className="px-2 py-0.5 rounded-full text-xs font-semibold"
                                style={{ background: "rgba(14, 165, 233, 0.12)", color: "#0369a1" }}
                            >
                                {meetup.month}
                            </span>
                            {meetup.comingSoon && (
                                <span
                                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                                    style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", color: "white" }}
                                >
                                    Notes Pending
                                </span>
                            )}
                        </div>
                        <h3 className="text-lg font-bold mb-2" style={{ color: "#1e293b" }}>
                            {meetup.title}
                        </h3>
                        <p className="text-sm m-0" style={{ color: "#64748b", lineHeight: 1.6 }}>
                            {meetup.intro}
                        </p>
                    </div>
                </div>
            </div>
        </Link>
    );
}

function MeetupDetailWrapper() {
    const { meetupId } = useParams();
    const meetup = monthlyMeetupsData.find((item) => item.id === meetupId);

    if (!meetup) {
        return (
            <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: "#ffffff" }}>
                <div className="max-w-4xl mx-auto">
                    <SubMenuWrapper>
                        <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Monthly Meetups"} to={"/strong-healthy/monthly-meetups"} />
                    </SubMenuWrapper>
                    <div className="text-center mt-8">
                        <span className="text-6xl mb-4 block">🔍</span>
                        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1e293b" }}>
                            Meetup Not Found
                        </h2>
                        <p className="text-base" style={{ color: "#64748b" }}>
                            The meetup you're looking for doesn't exist.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return <MeetupDetail meetup={meetup} />;
}

function MeetupDetail({ meetup }) {
    const embedUrl = getVimeoEmbedUrl(meetup.videoUrl);

    return (
        <div className="min-h-screen py-6 px-2 md:py-8 md:px-4" style={{ background: "#ffffff" }}>
            <div className="max-w-4xl mx-auto">
                <SubMenuWrapper>
                    <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Monthly Meetups"} to={"/strong-healthy/monthly-meetups"} />
                </SubMenuWrapper>

                <div className="text-center mb-6 mt-4">
                    <span
                        className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-3"
                        style={{
                            background: "linear-gradient(90deg, #0ea5e9 0%, #2563eb 100%)",
                            color: "white",
                        }}
                    >
                        {meetup.month}
                    </span>
                    <h1
                        className="text-3xl md:text-4xl font-extrabold mb-3"
                        style={{
                            background: "linear-gradient(90deg, #1e293b 0%, #334155 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        {meetup.title}
                    </h1>
                </div>

                {embedUrl ? (
                    <div className="mb-6">
                        <div
                            className="overflow-hidden rounded-3xl"
                            style={{
                                aspectRatio: "16 / 9",
                                border: "1px solid rgba(14, 165, 233, 0.16)",
                                boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
                            }}
                        >
                            <iframe
                                src={embedUrl}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                                title={`${meetup.month}: ${meetup.title}`}
                            />
                        </div>
                    </div>
                ) : (
                    <div
                        className="rounded-3xl p-6 md:p-8 mb-6 text-center"
                        style={{
                            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                            border: "1px solid rgba(148, 163, 184, 0.18)",
                        }}
                    >
                        <p className="text-base m-0" style={{ color: "#64748b" }}>
                            The recording link for this meetup will be added soon.
                        </p>
                    </div>
                )}

                {meetup.belowVideoLinks?.length > 0 && (
                    <MeetupResourceLinks links={meetup.belowVideoLinks} />
                )}

                <section
                    className="rounded-3xl p-4 sm:p-5 md:p-8"
                    style={{
                        background: "linear-gradient(135deg, #f8fafc 0%, #eef4ff 100%)",
                        border: "1px solid rgba(99, 102, 241, 0.14)",
                    }}
                >
                    <div className="mb-5">
                        <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: "#1e293b" }}>
                            Show Notes
                        </h2>
                        {meetup.intro && (
                            <p className="text-base md:text-lg mb-3" style={{ color: "#334155", lineHeight: 1.75 }}>
                                {meetup.intro}
                            </p>
                        )}
                        {meetup.focus && (
                            <p className="text-base md:text-lg m-0" style={{ color: "#334155", lineHeight: 1.75 }}>
                                {meetup.focus}
                            </p>
                        )}
                    </div>

                    {meetup.sections.length > 0 ? (
                        <div className="grid gap-5">
                            {meetup.sections.map((section) => (
                                <ShowNotesSection key={section.title} section={section} />
                            ))}
                        </div>
                    ) : (
                        <div
                            className="rounded-2xl p-4"
                            style={{
                                background: "rgba(255, 255, 255, 0.82)",
                                border: "1px solid rgba(148, 163, 184, 0.16)",
                            }}
                        >
                            <p className="m-0" style={{ color: "#64748b", lineHeight: 1.7 }}>
                                Full show notes for this meetup will be added soon.
                            </p>
                        </div>
                    )}

                    {meetup.whyWatch && (
                        <div
                            className="rounded-2xl p-4 md:p-5 mt-5"
                            style={{
                                background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                                border: "1px solid rgba(16, 185, 129, 0.2)",
                            }}
                        >
                            <h3 className="text-xl font-bold mb-3" style={{ color: "#065f46" }}>
                                Why Watch This Talk?
                            </h3>
                            <p className="m-0" style={{ color: "#064e3b", lineHeight: 1.7 }}>
                                {meetup.whyWatch}
                            </p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function MeetupResourceLinks({ links }) {
    return (
        <nav aria-label="Meetup resources" className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
            {links.map((link, index) => {
                const featured = index === 0;
                const className = "inline-flex items-center justify-center rounded-xl px-5 py-3 font-semibold no-underline transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";
                const style = featured
                    ? {
                        background: "linear-gradient(135deg, #006bc1 0%, #0e89ef 100%)",
                        color: "#ffffff",
                        boxShadow: "0 8px 22px rgba(0, 107, 193, 0.2)",
                    }
                    : {
                        background: "#ffffff",
                        color: "#0369a1",
                        border: "1px solid rgba(14, 165, 233, 0.28)",
                    };

                if (link.to) {
                    return (
                        <Link
                            key={link.label}
                            to={link.to}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={className}
                            style={style}
                        >
                            {link.label}
                        </Link>
                    );
                }

                return (
                    <a
                        key={link.label}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={className}
                        style={style}
                    >
                        {link.label}
                    </a>
                );
            })}
        </nav>
    );
}

function ShowNotesSection({ section }) {
    return (
        <section>
            <h3 className="text-xl md:text-2xl font-bold mb-3" style={{ color: "#1e293b" }}>
                {section.title}
            </h3>
            <div className="grid gap-3">
                {section.items.map((item, index) => (
                    <div
                        key={item.title}
                        className="rounded-2xl p-4"
                        style={{
                            background: "rgba(255, 255, 255, 0.86)",
                            border: "1px solid rgba(148, 163, 184, 0.16)",
                        }}
                    >
                        <h4 className="text-base md:text-lg font-bold mb-2" style={{ color: "#1e293b" }}>
                            {section.title === "Key Insights" && `${index + 1}. `}
                            {item.path ? (
                                <Link
                                    to={item.path}
                                    onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })}
                                    className="no-underline hover:underline"
                                    style={{ color: "#0369a1" }}
                                >
                                    {item.title}
                                </Link>
                            ) : (
                                item.title
                            )}
                        </h4>
                        <p className="m-0" style={{ color: "#475569", lineHeight: 1.7 }}>
                            {item.body}
                        </p>
                    </div>
                ))}
            </div>
        </section>
    );
}

function getVimeoEmbedUrl(videoUrl) {
    if (!videoUrl) return "";

    if (videoUrl.includes("player.vimeo.com")) {
        return videoUrl;
    }

    const match = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (!match) return videoUrl;

    return `https://player.vimeo.com/video/${match[1]}?title=0&byline=0&portrait=0`;
}
